import BaseComponent from '@/components/base';
import { getStyle } from '@/components/product-card/style.js';
import ProductVideo from '@/components/product-card/product-video';
import ProductImage from '@/components/product-card/product-image';

const BG_COLOR_DEFAULT = 'transparent';
const BG_COLOR_HOVER_DEFAULT = '#fbf5f0';
const BADGE_COLOR_DEFAULT = '#8ae5d3';
const CTA_BG_COLOR_DEFAULT = '#ffffff';
const CTA_BG_HOVER_COLOR_DEFAULT = '#000000';
const CTA_DISABLED_COLOR_DEFAULT = '#e6e8e8';

export default class ProductCard extends BaseComponent {
  onConstruct() {
    if (!customElements.get('product-video')) {
      customElements.define('product-video', ProductVideo);
    }

    if (!customElements.get('product-image')) {
      customElements.define('product-image', ProductImage);
    }
  }

  _handleClick = () => {
    if (this.url) {
      window.location.href = this.url;
    }
  };

  set handleClick(fn) {
    this._handleClick = fn;
  }

  get handleClick() {
    return this._handleClick;
  }

  onConnect() {
    const onClickHandler = (event) => {
      // If in redirect mode, use default redirection behavior
      if (this.hasAttribute('data-redirect-mode')) {
        if (this.url) {
          window.location.href = this.url;
        }
        return;
      }

      // Otherwise, use custom handleClick
      if (typeof this._handleClick === 'function') {
        this._handleClick(event);
      }
    };

    const ctaBtn = this.shadowRoot.querySelector('#cta');
    if (ctaBtn) {
      ctaBtn.addEventListener('click', onClickHandler);
    }
  }

  get title() {
    return this.getAttribute('title') || '';
  }

  get price() {
    return this.getAttribute('price') || '';
  }

  get priceLabel() {
    return this.getAttribute('price-label') || '';
  }

  get imageSources() {
    const imageSourcesAttr = this.getAttribute('image-sources');
    if (imageSourcesAttr) {
      return imageSourcesAttr;
    } else {
      const imageUrl = this.getAttribute('image-url') || '';
      return JSON.stringify({
        small: imageUrl,
        medium: imageUrl,
        large: imageUrl
      });
    }
  }

  get imageAlt() {
    return this.getAttribute('image-alt') || '';
  }

  get hoverVideo() {
    return this.getAttribute('hover-video') || '';
  }

  get hoverVideoPreview() {
    return this.getAttribute('hover-video-preview') || '';
  }

  get url() {
    return this.getAttribute('url') || '';
  }

  get badge() {
    return this.getAttribute('badge') || '';
  }

  get soldOut() {
    return this.getAttribute('sold-out') === 'true';
  }

  get bgColor() {
    return this.getAttribute('bg-color') || BG_COLOR_DEFAULT;
  }

  get bgColorHover() {
    return this.getAttribute('bg-color-hover') || BG_COLOR_HOVER_DEFAULT;
  }

  get badgeColor() {
    return this.getAttribute('badge-color') || BADGE_COLOR_DEFAULT;
  }

  get ctaDisabled() {
    return this.getAttribute('cta-disabled') === 'true';
  }

  get ctaDisabledColor() {
    return (
      this.getAttribute('cta-disabled-color') || CTA_DISABLED_COLOR_DEFAULT
    );
  }

  get ctaPrefix() {
    return this.getAttribute('cta-prefix') || '';
  }

  get ctaSuffix() {
    return this.getAttribute('cta-suffix') || '';
  }

  get ctaBgColor() {
    return this.getAttribute('cta-bg-color') || CTA_BG_COLOR_DEFAULT;
  }

  get ctaBgHoverColor() {
    return (
      this.getAttribute('cta-bg-hover-color') || CTA_BG_HOVER_COLOR_DEFAULT
    );
  }

  get productType() {
    return this.getAttribute('product-type') || '';
  }

  get style() {
    return getStyle({
      bgColor: this.bgColor,
      bgColorHover: this.bgColorHover,
      badgeColor: this.badgeColor,
      ctaBgColor: this.ctaBgColor,
      ctaBgHoverColor: this.ctaBgHoverColor,
      ctaDisabledColor: this.ctaDisabledColor,
      hasVideo: !!this.hoverVideo,
      isMobile: this.isSmall
    });
  }

  render() {
    return `
      ${this.style}
      <div class="main-wrapper">
        <a class="image-wrapper" href="${this.url}">
          ${
            this.hoverVideo
              ? `
            <product-video
              video-src="${this.hoverVideo}"
              video-preview-img="${this.hoverVideoPreview}"
            ></product-video>
          `
              : ''
          }
          <product-image
            image-sources='${this.imageSources}'
            image-alt="${this.title}"
          ></product-image>
          ${this.badge ? `<span class="badge">${this.badge}</span>` : ''}
          ${this.soldOut ? `<span class="badge sold-out">Sold Out</span>` : ''}
        </a>

        <div class="bottom-wrapper">
          ${
            this.productType
              ? `<span class="product-type">${this.productType}</span>`
              : ''
          }
          <div class="info-wrapper">
            <span class="title">${this.title}</span>
            <span class="price">${this.priceLabel} ${this.price}</span>
          </div>
          ${
            this.ctaPrefix
              ? `
            <button class="cta" id="cta" ${
              this.ctaDisabled ? 'disabled' : ''
            }>${this.ctaPrefix} ${
              this.ctaSuffix
                ? `<span class="line"></span>${this.ctaSuffix}`
                : ''
            }</button>`
              : ''
          }
        </div>
      </div>
    `;
  }
}
